import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, where, onSnapshot, getDocs, deleteDoc, doc, serverTimestamp, orderBy, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';

const getNextOccurrence = (item) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if ((!item.frequency || item.frequency === 'none') && !item.days) return item.date; 
  if (item.frequency === 'weekly' || item.frequency === 'monthly') {
    let d = new Date(item.date + 'T00:00:00');
    while (d < today) {
      if (item.frequency === 'weekly') d.setDate(d.getDate() + 7);
      if (item.frequency === 'monthly') d.setMonth(d.getMonth() + 1);
    }
    return d.toISOString().split('T')[0];
  }
  if (item.days && item.days.length > 0) {
    const dayMap = { 'Su': 0, 'Mo': 1, 'Tu': 2, 'We': 3, 'Th': 4, 'Fr': 5, 'Sa': 6 };
    const targetDays = item.days.map(d => dayMap[d]); 
    let d = new Date();
    d.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      if (targetDays.includes(d.getDay())) return d.toISOString().split('T')[0];
      d.setDate(d.getDate() + 1);
    }
  }
  return item.date;
};

const fetchGoogleEvents = async (accessToken, calendarId, showPast) => {
  const date = new Date();
  if (!showPast) {
    date.setHours(0, 0, 0, 0);
  } else {
    date.setDate(date.getDate() - 30);
  }
  
  const timeMin = date.toISOString();
  try {
    const encodedId = encodeURIComponent(calendarId);
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodedId}/events?timeMin=${timeMin}&maxResults=25&singleEvents=true&orderBy=startTime`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const data = await response.json();
    return data.items || [];
  } catch (err) {
    console.error("Fetch error:", err);
    return [];
  }
};

const FamilyCalendar = () => {
  const [masterEvents, setMasterEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [frequency, setFrequency] = useState('none');
  const [editingEventId, setEditingEventId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const { user, googleToken, getAvailableCalendars } = useAuth(); 
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [calendarOptions, setCalendarOptions] = useState([]);
  const [selectedCalIds, setSelectedCalIds] = useState(['primary']);
  const [showPast, setShowPast] = useState(false);
  const [gEvents, setGEvents] = useState([]);

  useEffect(() => {
    if (!user || !googleToken) return;

    // EFFECT 1: Fetch Google Data
    const syncGoogle = async () => {
      try {
        const results = await Promise.all(selectedCalIds.map(id => fetchGoogleEvents(googleToken, id)));
        const formatted = results.flat().map(ge => ({
          id: ge.id,
          title: ge.summary,
          effectiveDate: (ge.start.dateTime || ge.start.date).split('T')[0],
          source: 'Google',
          displayTitle: `🗓️ ${ge.summary}`,
          link: ge.htmlLink,
          time: ge.start.dateTime ? new Date(ge.start.dateTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'All Day'
        }));
        setGEvents(formatted);
      } catch (e) { console.error("Google Sync Failed", e); }
    };

    syncGoogle();
  }, [googleToken, selectedCalIds, showPast]);

  // EFFECT 2: Sync Source Options (The "Checkboxes" fix)
  useEffect(() => {
    if (showSyncSettings && googleToken) {
      getAvailableCalendars().then(setCalendarOptions);
    }
  }, [showSyncSettings, googleToken, getAvailableCalendars]);

  // EFFECT 3: Retrieving previously saved settings
  useEffect(() => {
    const loadSavedSettings = async () => {
      if (!user) return;
      const docRef = doc(db, 'users', user.uid, 'settings', 'calendar');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists() && docSnap.data().selectedCalIds) {
        setSelectedCalIds(docSnap.data().selectedCalIds);
      }
    };
    loadSavedSettings();
  }, [user]);

  // EFFECT 4: Main Firebase Listener
  useEffect(() => {
    if (!user) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (showSyncSettings && googleToken) {
      getAvailableCalendars().then(setCalendarOptions);
    }

    const qCalendar = query(collection(db, 'calendar'), where('userId', '==', user.uid), orderBy('date', 'asc'));

    const unsubscribe = onSnapshot(qCalendar, async (snapshot) => {
      // 1. Local Events with global past filter
      const calendarEvents = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          effectiveDate: getNextOccurrence(data),
          source: data.kidName ? 'Appointment' : 'General',
          displayTitle: data.kidName ? `${data.kidName}: ${data.title}` : data.title
        };
      }).filter(ev => showPast || new Date(ev.effectiveDate + 'T00:00:00') >= today);

      // 2. Kids Activities with global past filter
      const kidsQuery = query(collection(db, 'kids'), where('userId', '==', user.uid));
      const kidsSnaps = await getDocs(kidsQuery);
      let recurringActivities = [];

      for (const kidDoc of kidsSnaps.docs) {
        const kidName = kidDoc.data().name;
        const routSnap = await getDocs(collection(db, 'kids', kidDoc.id, 'routines'));
        const acts = routSnap.docs
          .filter(d => d.data().category === 'activity')
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              title: `${kidName}: ${data.activity}`,
              effectiveDate: getNextOccurrence(data),
              source: 'Activity',
              frequency: data.days && data.days.length > 0 ? 'weekly' : (data.frequency || 'none')
            };
          })
          .filter(act => showPast || new Date(act.effectiveDate + 'T00:00:00') >= today);
        recurringActivities = [...recurringActivities, ...acts];
      }

      // Merge EVERYTHING
      const combined = [...calendarEvents, ...recurringActivities, ...gEvents].sort((a, b) => 
        a.effectiveDate.localeCompare(b.effectiveDate)
      );

      setMasterEvents(combined);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, gEvents, showPast]);

  const toggleCalendar = async (calId) => {
    const newSelection = selectedCalIds.includes(calId) 
      ? (selectedCalIds.length > 1 ? selectedCalIds.filter(id => id !== calId) : selectedCalIds)
      : [...selectedCalIds, calId];
    
    setSelectedCalIds(newSelection);
    if (user) await setDoc(doc(db, 'users', user.uid, 'settings', 'calendar'), { selectedCalIds: newSelection }, { merge: true });
  };

  const handleAddEvent = async () => {
    if (!eventName || !eventDate || !user) return;
    await addDoc(collection(db, 'calendar'), { title: eventName, date: eventDate, frequency, userId: user.uid, createdAt: serverTimestamp() });
    setEventName(''); setEventDate(''); setFrequency('none'); setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Remove this event?")) await deleteDoc(doc(db, 'calendar', id));
  };

  const handleUpdateEvent = async (id) => {
    if (!editName || !editDate) return;
    await setDoc(doc(db, 'calendar', id), { title: editName, date: editDate }, { merge: true });
    setEditingEventId(null);
  };

  const startEditing = (event) => {
    setEditingEventId(event.id);
    setEditName(event.title);
    setEditDate(event.date);
  };

  const getSourceStyle = (source) => {
    switch (source) {
      case 'Appointment': return 'bg-purple-50 text-purple-700 border-purple-100';
      case 'Activity': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'Google': return 'bg-amber-50 text-amber-700 border-amber-100';
      default: return 'bg-indigo-50 text-indigo-700 border-indigo-100';
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading master schedule...</div>;

  return (
    <div className="max-w-4xl mx-auto p-3 md:p-4 pb-20 print:max-w-full print:p-0">
      
      {/* HEADER */}
      <header className="bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 mb-6 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight">Family Calendar</h2>
            <p className="text-gray-400 font-bold text-[10px] md:text-xs uppercase tracking-widest mt-1">
              Everything in one place.
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="flex gap-2 flex-1 sm:flex-none">
              <button onClick={() => window.print()} className="flex-1 sm:flex-none p-2.5 rounded-xl border border-gray-200 text-gray-400 active:bg-gray-50 flex justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
              </button>
              <button onClick={() => setShowPast(!showPast)} className={`flex-1 sm:flex-none p-2.5 rounded-xl border flex justify-center ${showPast ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-white text-gray-400 border-gray-200'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3" /><circle cx="12" cy="12" r="9" /><path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5" /></svg>
              </button>
              <button onClick={() => setShowSyncSettings(!showSyncSettings)} className={`flex-1 sm:flex-none p-2.5 rounded-xl border flex justify-center ${showSyncSettings ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-400 border-gray-200'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
            </div>
            <button onClick={() => setShowForm(!showForm)} className="w-full sm:w-auto bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-md active:scale-[0.98] transition-all">
              {showForm ? 'Cancel' : '+ Event'}
            </button>
          </div>
        </div>
      </header>

      {/* SYNC SETTINGS */}
      {showSyncSettings && (
        <div className="mb-6 bg-blue-50 p-5 rounded-2xl border border-blue-100 print:hidden animate-in fade-in slide-in-from-top-2">
          <h4 className="text-sm font-bold text-blue-800 mb-4 tracking-tight uppercase text-[10px]">Sync Sources</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {calendarOptions.map(cal => (
              <label key={cal.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border cursor-pointer hover:border-blue-300 transition-colors">
                <input type="checkbox" checked={selectedCalIds.includes(cal.id)} onChange={() => toggleCalendar(cal.id)} className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" />
                <span className="text-sm font-medium truncate">{cal.summary}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* FORM */}
      {showForm && (
        <div className="mb-8 bg-white p-5 rounded-2xl border border-indigo-100 shadow-xl animate-in fade-in slide-in-from-top-4">
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 tracking-widest uppercase ml-1">Event Name</label>
              <input className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-base transition-all" placeholder="Pizza Night 🍕" value={eventName} onChange={e => setEventName(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 tracking-widest uppercase ml-1">Date</label>
                <input className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white text-base outline-none" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 tracking-widest uppercase ml-1">Recurrence</label>
                <select className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white text-base outline-none" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                  <option value="none">One-time</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
          </div>
          <button onClick={handleAddEvent} className="w-full mt-6 bg-indigo-600 text-white py-3.5 rounded-xl font-bold shadow-lg shadow-indigo-100 active:bg-indigo-700 transition">Save to Calendar</button>
        </div>
      )}

      {/* EVENT LIST */}
      <div className="space-y-3 print:space-y-2">
        {masterEvents.map((event) => {
          if (editingEventId === event.id) {
            return (
              <div key={event.id} className="bg-indigo-50 p-4 rounded-2xl border border-indigo-200 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input className="flex-1 p-2 border rounded-lg text-base" value={editName} onChange={e => setEditName(e.target.value)} />
                  <input className="p-2 border rounded-lg text-base" type="date" value={editDate} onChange={e => setEditDate(e.target.value)} />
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setEditingEventId(null)} className="text-gray-500 text-sm font-bold">Cancel</button>
                  <button onClick={() => handleUpdateEvent(event.id)} className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-bold shadow-sm">Save Changes</button>
                </div>
              </div>
            );
          }

          const dateObj = new Date(event.effectiveDate + 'T00:00:00');
          return (
            <div key={event.id} onClick={() => event.link && window.open(event.link, '_blank')} className="flex items-center gap-4 bg-white p-3 md:p-4 rounded-2xl border border-gray-100 shadow-sm active:bg-gray-50 transition-all">
              <div className={`flex flex-col items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-xl border-2 font-bold flex-shrink-0 ${getSourceStyle(event.source)}`}>
                  <span className="text-[9px] md:text-[10px] uppercase leading-none mb-1">{dateObj.toLocaleDateString('en-US', { month: 'short' })}</span>
                  <span className="text-lg md:text-xl leading-none">{dateObj.getDate()}</span>
              </div>   
              
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800 text-sm md:text-base truncate">{event.displayTitle || event.title}</h3>
                <div className="text-[10px] md:text-xs text-gray-500 font-medium flex items-center gap-2 mt-0.5">
                  {event.source === 'Google' ? (
                    <span className="flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-amber-400" /> {event.time}</span>
                  ) : (
                    <>
                      <span>{dateObj.toLocaleDateString('en-US', { weekday: 'long' })}</span>
                      {event.frequency && event.frequency !== 'none' && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-600 font-bold text-[9px] uppercase tracking-wider border border-indigo-100">
                          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
                          {event.frequency}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              {event.source === 'General' && (
                <div className="flex gap-1 border-l pl-2 print:hidden">
                  <button onClick={(e) => { e.stopPropagation(); startEditing(event); }} className="p-2 text-gray-300 hover:text-blue-600 transition-colors active:text-indigo-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }} className="p-2 text-gray-300 hover:text-red-600 transition-colors active:text-red-500 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .bg-white { background-color: white !important; }
          .shadow-sm { box-shadow: none !important; }
          .bg-purple-50, .bg-emerald-50, .bg-blue-50, .bg-indigo-50 { 
            background-color: transparent !important; 
            border: 1px solid #e5e7eb !important;
          }
        }
      `}} />
    </div>
  );
};

export default FamilyCalendar;